import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { updateReservation, getReservationDetail } from 'api/reservationApi';
import { getSessionStorage } from 'utility/browserStorageUtil';
import useStyles from './styles';

const DAY_OF_WEEK_JA = ['日', '月', '火', '水', '木', '金', '土'];

function formatDesiredTime(isoStr: string, treatmentTime: number): string {
  const start = moment(isoStr);
  const dow = DAY_OF_WEEK_JA[start.day()];
  return `${start.format('YYYY年MM月DD日')}（${dow}）${start.format('HH:mm')}（日本時間）〜（約${treatmentTime}分）`;
}

const useDialogStyles = makeStyles((theme: Theme) => createStyles({
  dialogTitle: {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '1.1rem',
    padding: '20px 24px 12px',
  },
  dialogMessage: {
    textAlign: 'center',
    fontSize: '0.95rem',
    color: '#333',
    padding: '4px 24px 20px',
  },
  dialogActions: {
    display: 'flex',
    borderTop: '1px solid #e0e0e0',
  },
  dialogBtn: {
    flex: 1,
    padding: '14px 0',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    '&:first-child': { borderRight: '1px solid #e0e0e0' },
    '&:hover': { backgroundColor: '#fafafa' },
  },
}));

interface CustomerReservation {
  reservationId: string;
  desiredTime: string;
  orgName: string;
  orgId: string;
  contractCourseId: string;
}

interface DetailData {
  companyName: string;
  orgName: string;
  courseName: string;
  treatmentTime: number;
  managementNumber: string;
  reservationNumber: string;
  customerLastName: string;
  customerFirstName: string;
  desiredTime: string;
  customerChangeHour?: number | null;
  customerCancelHour?: number | null;
}

interface LocationState {
  reservation?: CustomerReservation;
}

function MypageReservationChange() {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const companyCode = getSessionStorage('companyCode');
  const reservation = location.state?.reservation;

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [detail, setDetail] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reservation?.reservationId) return;
    setLoading(true);
    getReservationDetail(reservation.reservationId, companyCode)
      .then((res) => { setDetail(res); })
      .catch(() => {
        enqueueSnackbar('予約詳細の取得に失敗しました。', { variant: 'error' });
      })
      .finally(() => { setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancelConfirm = async () => {
    if (!reservation) return;
    setCancelling(true);
    try {
      await updateReservation(reservation.reservationId, companyCode, { cancelFlag: true, cancelType: 1 });
      setCancelDialogOpen(false);
      history.push(`/${companyCode}/mypage/customer/reservationChangeComplete?type=cancel`);
    } catch {
      enqueueSnackbar('キャンセルに失敗しました。もう一度お試しください。', { variant: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  const handleDateChangeClick = () => {
    history.push({
      pathname: `/${companyCode}/mypage/customer/reservationChangeDateChange`,
      state: { reservation, detail },
    });
  };

  const handleMenuReselect = () => {
    // R-4 対応: 元予約 ID を URL クエリで渡す。
    // 飛び先 (reservationBookingOnlineV2/index.tsx) が初回ロード時に
    // urlSearchParam.get('reservationId') を取得して Context (originalReservationId) に保存し、
    // 以降のステップ遷移で URL クエリが置き換わっても Context が保持される。
    history.push(`/${companyCode}/mypage/contact/reserve?reservationId=${reservation?.reservationId || ''}`);
  };

  const effectiveDesiredTime = detail?.desiredTime || reservation?.desiredTime;
  const { isChangeDisabled, isCancelDisabled } = useMemo(() => {
    if (!effectiveDesiredTime) {
      return { isChangeDisabled: false, isCancelDisabled: false };
    }
    const hoursUntilDesired = (moment(effectiveDesiredTime).valueOf() - Date.now()) / 3600000;
    return {
      isChangeDisabled: !!detail?.customerChangeHour && hoursUntilDesired < detail.customerChangeHour,
      isCancelDisabled: !!detail?.customerCancelHour && hoursUntilDesired < detail.customerCancelHour,
    };
  }, [effectiveDesiredTime, detail]);

  const handleCancelClick = () => {
    if (isCancelDisabled) return;
    setCancelDialogOpen(true);
  };

  if (!reservation) {
    return (
      <div className={classes.root}>
        <AppBar position="static" className={classes.appBar}>
          <Toolbar>
            <Typography variant="h6" className={classes.appBarTitle}>予約詳細</Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" className={classes.container}>
          <Typography>予約情報が見つかりません。</Typography>
        </Container>
      </div>
    );
  }

  const orgName = detail?.orgName || reservation.orgName;
  const treatmentTime = detail?.treatmentTime || 0;
  const desiredTime = detail?.desiredTime || reservation.desiredTime;

  return (
    <div className={classes.root}>
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth={true}>
        <DialogContent style={{ padding: 0 }}>
          <p className={dialogClasses.dialogTitle}>確認</p>
          <p className={dialogClasses.dialogMessage}>予約をキャンセルしますがよろしいですか？</p>
          <div className={dialogClasses.dialogActions}>
            <button
              type="button"
              className={dialogClasses.dialogBtn}
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
            >
              いいえ
            </button>
            <button
              type="button"
              className={dialogClasses.dialogBtn}
              onClick={handleCancelConfirm}
              disabled={cancelling}
            >
              はい
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AppBar position="static" className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" className={classes.appBarTitle}>
            {orgName}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" className={classes.container}>
        <Typography className={classes.pageTitle}>予約詳細</Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Box className={classes.salonInfoBox}>
              {detail?.companyName && (
                <Typography className={classes.salonName}>{detail.companyName}</Typography>
              )}
              <Typography className={classes.branchName}>{orgName}</Typography>
              {detail?.courseName && (
                <Typography className={classes.courseName}>{detail.courseName}</Typography>
              )}
            </Box>

            <Box className={classes.dateTimeRow}>
              <Typography component="span" className={classes.dateTimeLabel}>予約日時</Typography>
              <Typography component="span" variant="body2">
                {formatDesiredTime(desiredTime, treatmentTime)}
              </Typography>
            </Box>

            <Divider className={classes.divider} />

            <Typography className={classes.sectionTitle}>予約者</Typography>
            <Box className={classes.reserverBox}>
              <Box className={classes.reserverRow}>
                <Typography component="span" className={classes.reserverLabel}>予約番号：</Typography>
                <Typography component="span" className={classes.reserverValue}>
                  {detail?.reservationNumber || ''}
                </Typography>
              </Box>
              <Box className={classes.reserverRow}>
                <Typography component="span" className={classes.reserverLabel}>氏名　　：</Typography>
                <Typography component="span" className={classes.reserverValue}>
                  {`${detail?.customerLastName || ''} ${detail?.customerFirstName || ''}`}
                </Typography>
              </Box>
            </Box>

            <Box className={classes.changeSection}>
              <Typography className={classes.changeSectionTitle}>予約内容の変更</Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth={true}
                className={classes.actionButton}
                disabled={isChangeDisabled}
                onClick={handleDateChangeClick}
              >
                日時を変更
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth={true}
                className={classes.actionButton}
                disabled={isChangeDisabled}
                onClick={handleMenuReselect}
              >
                メニューから再選択
              </Button>
              <Typography
                component="span"
                className={classes.cancelLink}
                style={isCancelDisabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
                onClick={handleCancelClick}
              >
                予約内容のキャンセル
              </Typography>
              {(isChangeDisabled || isCancelDisabled) && (
                <Typography
                  variant="body2"
                  style={{
                    color: '#d32f2f',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginTop: 16,
                  }}
                >
                  {`※${[
                    isChangeDisabled && '変更受付時間',
                    isCancelDisabled && 'キャンセル受付時間',
                  ].filter(Boolean).join('、')}を過ぎました`}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Container>
    </div>
  );
}

export default MypageReservationChange;
